import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const SUIVI_STATUS = ['Sous observation', 'Guéri', 'Orienté', 'À surveiller'];
const EMPTY = {
  studentId: '', reportedDisease: '', symptoms: '', careGiven: '',
  medicineUsed: '', quantityUsed: '', reportDate: new Date().toISOString().slice(0, 10),
  evolution: '', followUpStatus: 'Sous observation', referredTo: '', responsibleContact: '',
};

export default function Sante() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState('');

  function load() {
    api.get('/registry/health').then((d) => setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return !q || (r.studentName || '').toLowerCase().includes(q) || (r.reportedDisease || '').toLowerCase().includes(q);
  });

  const STATUS_COLOR = { 'Sous observation': 'gold', 'Guéri': 'green', 'Orienté': 'navy', 'À surveiller': 'orange' };

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Dossiers</span><strong>{rows.length}</strong></div>
        <div className="kpi gold"><span>Sous observation</span><strong>{rows.filter((r) => r.followUpStatus === 'Sous observation').length}</strong></div>
        <div className="kpi green"><span>Guéris</span><strong>{rows.filter((r) => r.followUpStatus === 'Guéri').length}</strong></div>
        <div className="kpi navy"><span>Orientés</span><strong>{rows.filter((r) => r.followUpStatus === 'Orienté').length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Suivi santé des élèves</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="search-input" placeholder="Nom, maladie…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Nouveau dossier</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun dossier santé.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Élève</th><th>Pathologie</th><th>Date</th><th>Soins</th><th>Médicament</th><th>Évolution</th><th>Statut</th><th></th></tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.studentName}</b><br /><small>{r.className}</small></td>
                  <td>{r.reportedDisease}</td>
                  <td className="mono">{r.reportDate ? new Date(r.reportDate).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{r.careGiven || '—'}</td>
                  <td>{r.medicineUsed || '—'}{r.quantityUsed ? ` (${r.quantityUsed})` : ''}</td>
                  <td>{r.evolution || '—'}</td>
                  <td><span className={`badge ${STATUS_COLOR[r.followUpStatus] || 'gray'}`}>{r.followUpStatus || '—'}</span></td>
                  <td>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && (
        <SanteModal
          initial={modal}
          students={students}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

function SanteModal({ initial, students, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function onStudentChange(e) {
    const id = e.target.value;
    setF((p) => ({ ...p, studentId: id }));
    const s = students.find((s) => s.id === id);
    if (s) {
      setStudentInfo(s);
    } else {
      setStudentInfo(null);
    }
  }

  async function save() {
    if (!f.reportedDisease) { setError('La pathologie est obligatoire.'); return; }
    setSaving(true);
    try {
      const student = students.find((s) => s.id === f.studentId);
      const payload = { ...f, studentName: student ? `${student.lastName} ${student.firstName}` : f.studentName };
      if (isEdit) await api.patch(`/registry/health/${initial.id}`, { data: payload });
      else await api.post('/registry/health', { data: payload, label: payload.studentName || payload.designation || payload.reportedDisease || payload.formType || payload.eventType || '', searchText: JSON.stringify(payload).slice(0, 200) });
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier le dossier' : 'Nouveau dossier santé'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="fieldset-title">Identification</div>
        <div className="grid-3">
          <div className="field"><label>Élève</label>
            <select value={f.studentId} onChange={onStudentChange}>
              <option value="">Sélectionner…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Date signalement</label><input type="date" value={f.reportDate} onChange={set('reportDate')} /></div>
          <div className="field"><label>Contact responsable</label>
            <input value={f.responsibleContact || (studentInfo?.ppcbPhone || '')} onChange={set('responsibleContact')} placeholder={studentInfo?.ppcbPhone || ''} />
          </div>
        </div>

        {studentInfo && (
          <div style={{ background: 'var(--surface-soft, #f8f9fa)', border: '1px solid var(--line)', borderRadius: 6, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 16px', fontSize: '0.85rem' }}>
              <div><span style={{ color: 'var(--muted)' }}>Classe</span><br /><b>{studentInfo.className || studentInfo.classId || '—'}</b></div>
              <div><span style={{ color: 'var(--muted)' }}>Âge</span><br /><b>{studentInfo.age || (studentInfo.birthYear ? new Date().getFullYear() - Number(studentInfo.birthYear) : '—')} ans</b></div>
              <div><span style={{ color: 'var(--muted)' }}>Groupe sanguin</span><br /><b>{studentInfo.bloodGroup || '—'}</b></div>
              <div><span style={{ color: 'var(--muted)' }}>Électrophorèse</span><br /><b>{studentInfo.electrophoresis || '—'}</b></div>
              <div><span style={{ color: 'var(--muted)' }}>Parent / Tuteur</span><br /><b>{studentInfo.ppcbName || '—'}</b></div>
              <div><span style={{ color: 'var(--muted)' }}>Contact parent</span><br /><b>{studentInfo.ppcbPhone || '—'}</b></div>
            </div>
          </div>
        )}

        <div className="grid-3">
        </div>
        <div className="fieldset-title">Pathologie & soins</div>
        <div className="grid-3">
          <div className="field"><label>Pathologie / Maladie *</label><input value={f.reportedDisease} onChange={set('reportedDisease')} /></div>
          <div className="field"><label>Médicament utilisé</label><input value={f.medicineUsed} onChange={set('medicineUsed')} /></div>
          <div className="field"><label>Quantité utilisée</label><input value={f.quantityUsed} onChange={set('quantityUsed')} /></div>
        </div>
        <div className="field"><label>Symptômes</label><textarea value={f.symptoms} onChange={set('symptoms')} rows={2} /></div>
        <div className="field"><label>Soins administrés</label><textarea value={f.careGiven} onChange={set('careGiven')} rows={2} /></div>
        <div className="fieldset-title">Suivi</div>
        <div className="grid-3">
          <div className="field"><label>Statut suivi</label>
            <select value={f.followUpStatus} onChange={set('followUpStatus')}>
              {SUIVI_STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Référence / Évacuation</label><input value={f.referredTo} onChange={set('referredTo')} /></div>
        </div>
        <div className="field"><label>Évolution</label><textarea value={f.evolution} onChange={set('evolution')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
